import React, { useEffect, useState, useMemo, useCallback } from "react";
import endpoints from "@config/endpoints";
import { getData } from "@services/CommonApiService";
import { RootState } from "@store/reducers";
import { useSelector, useDispatch } from "react-redux";
import { setChildBenefList } from "@store/actions/beneficiaryAction";
import './ChildList.css'
import ChildListCard from "./ChildListCard";
import Button from "@components/common/Button";
import { encodeWithKeyAndShift } from "@utils/base64Utils";
import { useNavigate } from "react-router-dom";
import { errorLogger } from "@utils/applicationLogUtils";
import { formatDateBasedOnTimeZone } from "@utils/commonUtils";
import { buttonNames } from "@constants/appConstants";
import { COMMON_CONSTANTS } from '@constants/index';
import convertByteToImageURL from "@utils/convertByteToImageURL";

const ChildList = () => {
    const dispatch = useDispatch();
    const benfDetails = useSelector((state: RootState) => state.beneficiary.beneficiaryDetails);
    const reduxChildList = useSelector((state: RootState) => state.beneficiary.childBenefList);
    const benefId = benfDetails?.[0]?.benef_id;
    const isDead = benfDetails?.[0]?.is_dead;
    const [childDetails, setChildDetails] = useState<any>([]);
    const childCount = childDetails?.length ?? 0;
    const [beneficiary, setBeneficiary] = useState<any>();
    const userRole = benfDetails?.user_role || localStorage.getItem('userTypeName');

    const navigate = useNavigate();

    useEffect(() => {
        setBeneficiary(benfDetails?.[0]);
    }, [benfDetails]);

    useEffect(() => {
        if (!benefId) return; 

        let isMounted = true;

        const toChildrenWithImgs = (children: any[]) =>
            (children ?? []).map((c: any) => {
              const imgUrl = c?.image_data?.data ? convertByteToImageURL(c.image_data.data) : null;
              return { ...c, img_url: imgUrl };
            });

        if (Array.isArray(reduxChildList) && reduxChildList.length > 0) {
            setChildDetails(toChildrenWithImgs(reduxChildList));
            return () => { isMounted = false; };
        }

        const fetchData = async () => {
            try {
                const childRes =await getData<any>(`${endpoints.getChildBeneficiaries}/${benefId}`);
                const children = childRes?.data?.[0] || [];

                const childrenWithImgs = toChildrenWithImgs(children);

                if (!isMounted) return;

                setChildDetails(childrenWithImgs);
                const existing = reduxChildList || [];

                const hasSameLength = existing.length === children.length;
                const isSameById =
                    hasSameLength &&
                    existing.every((c: any, idx: number) => c?.child_benef_id === children[idx]?.child_benef_id);

                if (!isSameById) {
                    dispatch(setChildBenefList(childrenWithImgs));
                }
            } catch (error: any) {
                errorLogger(error?.response?.data || error?.response || error);
            }
        };

        fetchData();
        return () => {
            isMounted = false;
        };
    }, [benefId, dispatch]);

    const childDobs = useMemo(() => {
        return childDetails?.map((item: any) => formatDateBasedOnTimeZone(item?.dob)) ?? [];
      }, [childDetails]);
      
      const childBalABHAIds = useMemo(() => {
        return childDetails?.map((item: any) => item.bal_abha_id) ?? [];
      }, [childDetails]);

      const handleAddNewChild = useCallback(() => {
        const reqData = {
          beneficiary,
          childDobs,
          beneficiaryDetails: benfDetails?.[0],
          childBalABHAIds,
        };
        const encodedData = encodeWithKeyAndShift(JSON.stringify(reqData));
        navigate(`/beneficiaries/create-new-child`, { state: encodedData });
      }, [beneficiary, childDobs, childBalABHAIds, benfDetails, navigate]);

    return (
        <div className="child-list-container">
            <div className="register-child-btn-container">
            {userRole !== COMMON_CONSTANTS.FIELD_LEVEL_USER && (
                <Button
                    onClick={!isDead ? handleAddNewChild : () => {}}
                    disabled={isDead}
                    id="save-details"
                    buttonName={buttonNames.registerAChild}
                    colorType={'primary'}
                />
            )}
            </div>
            <div className="registered-children-header">Registered Children</div>
            <div className="child-list">
            {childDetails?.map((child: any, index: number) => (
                <ChildListCard
                key={child?.child_benef_id ?? `${benefId}-${index}`}
                child={child}
                index={index}
                childCount={childCount}
                />
            ))}
            </div>
        </div>
    )
}

export default ChildList;